/**
 * QuickReserve — drop-in widget for any venue page.
 *
 * Usage:
 *   <QuickReserve venueSlug="coffee-commune" accent="#C75B3D" />
 *
 * Fetches venue meta from /api/reservations/venues/{slug}, validates the
 * form client-side, POSTs to /api/reservations, displays the confirmation
 * card with the HustleCRM-synced status badge and a "Plan my journey" stub link
 * (which the future TransLink calculator will wire into).
 */
import { useEffect, useState } from "react";
import axios from "axios";
import "./QuickReserve.css";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
export default function QuickReserve({ venueSlug, accent = "#C75B3D", title = "Reserve a table" }) {
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [depositPaid, setDepositPaid] = useState(false);
  const [depositBusy, setDepositBusy] = useState(false);
  const [depositError, setDepositError] = useState("");
  const [journey, setJourney] = useState(null);
  const [journeyBusy, setJourneyBusy] = useState(false);

  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: "", email: "", phone: "",
    booking_date: today,
    booking_time: "19:00",
    party_size: 2,
    notes: "",
  });

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/api/reservations/venues/${venueSlug}`)
      .then(({ data }) => { if (!cancelled) setVenue(data); })
      .catch(() => { /* venue unknown — keep widget usable with defaults */ });
    return () => { cancelled = true; };
  }, [venueSlug]);

  // Handle the Stripe success_url redirect — mark deposit paid locally.
  useEffect(() => {
    const url = new URL(window.location.href);
    const bk = url.searchParams.get("bk");
    if (bk && url.searchParams.get("deposit_ok") === "1") {
      axios.post(`${API}/api/reservations/${bk}/deposit/mark-paid`)
        .then(() => axios.get(`${API}/api/reservations/${bk}`))
        .then(({ data }) => {
          setResult({
            ok: true, booking_id: bk, venue: { name: data.venue_name, address: data.venue_address, policy: "" },
            arrive_by_iso: data.arrive_by_iso, ghl_synced: true,
            deposit: { required: true, amount_aud: data.deposit_amount_aud, rationale: "" },
          });
          setDepositPaid(true);
          // Restore party_size for display
          setForm((f) => ({ ...f, party_size: data.party_size }));
          // Clean the URL
          window.history.replaceState({}, "", url.pathname);
        })
        .catch(() => {});
    }
  }, []);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const payDeposit = async () => {
    setDepositBusy(true); setDepositError("");
    try {
      const { data } = await axios.post(
        `${API}/api/reservations/${result.booking_id}/deposit`,
        { origin: window.location.origin },
      );
      if (data.checkout_url) {
        window.location.assign(data.checkout_url);
        return;
      }
      if (data.no_deposit_needed || data.already_paid) {
        setDepositPaid(true);
      }
    } catch (e) {
      setDepositError(e?.response?.data?.detail || e.message || "Could not start deposit checkout.");
    } finally {
      setDepositBusy(false);
    }
  };

  const openJourney = async () => {
    setJourneyBusy(true);
    try {
      // Try to grab geolocation for accurate leave-by time. If denied,
      // we still call the API with venue-only args so the user sees the
      // deep-link + a fallback estimate.
      const pos = await new Promise((resolve) => {
        if (!navigator.geolocation) return resolve(null);
        navigator.geolocation.getCurrentPosition(
          (p) => resolve({ lat: p.coords.latitude, lng: p.coords.longitude }),
          () => resolve(null),
          { enableHighAccuracy: true, timeout: 6000 },
        );
      });
      const venueLat = result.venue?.lat ?? venue?.lat;
      const venueLng = result.venue?.lng ?? venue?.lng;
      const params = new URLSearchParams({
        arrive_by:  result.arrive_by_iso,
        venue_lat:  String(venueLat ?? -27.4698),
        venue_lng:  String(venueLng ?? 153.0251),
        venue_name: result.venue?.name || venue?.name || "the venue",
      });
      if (pos) {
        params.append("origin_lat", String(pos.lat));
        params.append("origin_lng", String(pos.lng));
        params.append("origin_label", "Your location");
      }
      const { data } = await axios.get(`${API}/api/translink/journey?${params.toString()}`);
      setJourney(data);
    } catch (e) {
      setJourney({ error: e.message || "Journey calc failed" });
    } finally {
      setJourneyBusy(false);
    }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/api/reservations`, {
        venue_slug: venueSlug,
        ...form,
        party_size: Number(form.party_size) || 2,
      });
      setResult(data);
    } catch (e2) {
      setError(e2?.response?.data?.detail || e2.message || "Could not save your booking — please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (result) {
    const arriveDt = new Date(result.arrive_by_iso);
    const formatted = arriveDt.toLocaleString("en-AU", {
      weekday: "long", day: "numeric", month: "long",
      hour: "numeric", minute: "2-digit", hour12: true,
      timeZone: "Australia/Brisbane",
    });
    const dep = result.deposit || {};
    // Deposit-required pathway — surface the hold-card flow before
    // declaring the booking fully confirmed.
    if (dep.required && !depositPaid) {
      return (
        <div className="qr-card qr-card--deposit" style={{ "--qr-accent": accent }} data-testid="qr-deposit-required">
          <div className="qr-eyebrow" style={{ color: "#a64423" }}>Holding deposit needed</div>
          <h3 className="qr-h">Almost there — a ${dep.amount_aud} hold secures your table</h3>
          <p className="qr-arrive">
            Arrive by <strong>{formatted}</strong> · party of {form.party_size}
          </p>
          <div className="qr-meta" style={{ background: "#fff5ed", borderColor: "#f3c4a3" }}>
            <div><span>Booking ID</span><strong>{result.booking_id}</strong></div>
            <div><span>Venue policy</span><strong style={{ textAlign: "right", maxWidth: 260 }}>{dep.rationale}</strong></div>
            <div><span>Deposit</span><strong>${dep.amount_aud} AUD · refunded on attendance</strong></div>
          </div>
          <p className="qr-policy">
            This venue requires a small holding deposit because of prior no-shows at <em>this venue</em>. Other venues never see this information.
          </p>
          {depositError && <div className="qr-error">{depositError}</div>}
          <div className="qr-actions">
            <button
              type="button"
              onClick={payDeposit}
              disabled={depositBusy}
              className="qr-btn qr-btn--primary"
              data-testid="qr-pay-deposit"
            >
              {depositBusy ? "Opening secure checkout…" : `Pay $${dep.amount_aud} deposit →`}
            </button>
            <button
              type="button"
              onClick={() => { setResult(null); setDepositPaid(false); }}
              className="qr-btn qr-btn--ghost"
              data-testid="qr-cancel-booking"
            >
              Cancel booking
            </button>
          </div>
        </div>
      );
    }
    return (
      <div className="qr-card qr-card--done" style={{ "--qr-accent": accent }} data-testid="qr-success">
        <div className="qr-eyebrow">{depositPaid ? "Confirmed · Deposit held" : "Confirmed"}</div>
        <h3 className="qr-h">You're booked at {result.venue?.name || "the venue"}</h3>
        <p className="qr-arrive">
          Arrive by <strong>{formatted}</strong> · party of {form.party_size}
        </p>
        <div className="qr-meta">
          <div><span>Booking ID</span><strong>{result.booking_id}</strong></div>
          <div><span>Address</span><strong>{result.venue?.address}</strong></div>
          {depositPaid && (
            <div className="qr-ghl"><span>Deposit</span><strong>✓ Held (${dep.amount_aud})</strong></div>
          )}
          {result.ghl_synced && (
            <div className="qr-ghl"><span>CRM sync</span><strong>✓ Live</strong></div>
          )}
        </div>
        <p className="qr-policy">{result.venue?.policy || ""}</p>
        <div className="qr-actions">
          <button
            type="button"
            className="qr-btn qr-btn--secondary"
            onClick={openJourney}
            disabled={journeyBusy}
            data-testid="qr-plan-journey"
          >
            {journeyBusy ? "Calculating route…" : "🚆 Plan my journey →"}
          </button>
          <button
            className="qr-btn qr-btn--ghost"
            onClick={() => { setResult(null); setForm({ ...form, name: "", email: "", phone: "", notes: "" }); }}
            data-testid="qr-book-another"
          >
            Book another
          </button>
        </div>
        {journey && (
          <div className="qr-journey" data-testid="qr-journey-result">
            <div className="qr-journey__hd">Leave by <strong>{journey.leave_by_local_brisbane}</strong></div>
            <div className="qr-journey__sub">
              {journey.distance_km != null ? `${journey.distance_km} km · ` : ""}
              {journey.total_minutes} min door-to-door
            </div>
            <ul className="qr-journey__legs">
              {(journey.legs || []).map((leg, i) => (
                <li key={i}>
                  <span className={`qr-leg qr-leg--${leg.mode}`}>{leg.mode}</span>
                  {leg.label} · {leg.minutes} min
                </li>
              ))}
            </ul>
            <a
              href={journey.deeplink}
              target="_blank"
              rel="noopener noreferrer"
              className="qr-btn qr-btn--secondary"
              data-testid="qr-open-translink"
              style={{ marginTop: 10 }}
            >
              Open in TransLink →
            </a>
            <p className="qr-policy" style={{ marginTop: 8 }}>{journey.advisory}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <form className="qr-card" onSubmit={submit} style={{ "--qr-accent": accent }} data-testid={`qr-widget-${venueSlug}`}>
      <div className="qr-eyebrow">Quick Reserve</div>
      <h3 className="qr-h">{title}</h3>
      {venue && (
        <p className="qr-sub">{venue.name} · {venue.hours}</p>
      )}

      <div className="qr-row qr-row--2">
        <label>
          <span>Date</span>
          <input
            type="date"
            value={form.booking_date}
            min={today}
            onChange={(e) => set("booking_date", e.target.value)}
            data-testid="qr-input-date"
            required
          />
        </label>
        <label>
          <span>Time</span>
          <input
            type="time"
            value={form.booking_time}
            onChange={(e) => set("booking_time", e.target.value)}
            data-testid="qr-input-time"
            required
          />
        </label>
      </div>

      <label className="qr-party">
        <span>Party size</span>
        <div className="qr-stepper" role="group" aria-label="Party size">
          {[1, 2, 3, 4, 5, 6, 8, 10, 12].filter((n) => !venue || (n >= venue.min_party && n <= venue.max_party)).map((n) => (
            <button
              key={n}
              type="button"
              className={`qr-stepper__btn ${Number(form.party_size) === n ? "is-active" : ""}`}
              onClick={() => set("party_size", n)}
              data-testid={`qr-party-${n}`}
            >
              {n}
            </button>
          ))}
        </div>
      </label>

      <div className="qr-row qr-row--1">
        <label>
          <span>Your name</span>
          <input
            value={form.name}
            onChange={(e) => set("name", e.target.value)}
            placeholder="Full name"
            data-testid="qr-input-name"
            required
          />
        </label>
      </div>

      <div className="qr-row qr-row--2">
        <label>
          <span>Email</span>
          <input
            type="email"
            value={form.email}
            onChange={(e) => set("email", e.target.value)}
            placeholder="you@example.com"
            data-testid="qr-input-email"
            required
          />
        </label>
        <label>
          <span>Mobile</span>
          <input
            value={form.phone}
            onChange={(e) => set("phone", e.target.value)}
            placeholder="04XX XXX XXX"
            data-testid="qr-input-phone"
            required
          />
        </label>
      </div>

      <label>
        <span>Notes (optional)</span>
        <textarea
          rows={2}
          value={form.notes}
          onChange={(e) => set("notes", e.target.value)}
          placeholder="Window table, dietary requirements, anniversary…"
          data-testid="qr-input-notes"
        />
      </label>

      {error && <div className="qr-error" data-testid="qr-error">{String(error)}</div>}

      <button
        type="submit"
        className="qr-btn qr-btn--primary"
        disabled={loading}
        data-testid="qr-submit"
      >
        {loading ? "Confirming…" : "Confirm reservation"}
      </button>

      {venue && (
        <p className="qr-fineprint">{venue.policy}</p>
      )}
    </form>
  );
}
